import React from "react";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { Calendar, CheckCircle2, Circle, Clock, Mail, MapPinCheckInside, Phone, UserPlus } from "lucide-react";
import VoiceUpdateButton from "../VoiceUpdateButton.jsx";

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return `${Math.floor(diff / 604800000)}w ago`;
};

const formatCreatedDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const LeadCardList = ({
  isInitialLoading,
  error,
  visibleLeads,
  getLatestComment,
  activeFilter,
  handleLeadClick,
  assignScrollLeadId,
  setSelectedLead,
  setIsAssignModalOpen,
  setSentinelRef,
  isFetchingMore,
  hasMore,
  // Multi-select props
  selectMode = false,
  selectedIds = [],
  onToggleSelect,
  onLeadUpdated,
}) => {
  return (
    <div className="space-y-3 sm:space-y-4">
      {isInitialLoading && (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <div className="w-8 h-8 border-2 border-[#00a8b4] border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-slate-500">Loading leads…</p>
        </div>
      )}

      {error && !isInitialLoading && (
        <div className="bg-rose-50/50 border border-rose-200/60 rounded-lg p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!isInitialLoading && !error && visibleLeads.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="text-sm text-slate-600">No leads found</div>
          <p className="text-xs text-slate-500 mt-1">
            Try adjusting your filters or search
          </p>
        </div>
      )}

      {visibleLeads.map((lead) => {
        const latestComment = getLatestComment(lead);
        const isSelected = selectedIds.includes(lead.id);

        return (
          <div
            key={lead.id}
            id={`lead-${lead.id}`}
            className="group cursor-pointer"
            onContextMenu={(e) => {
              e.preventDefault();
              if (selectMode) return;
              sessionStorage.setItem(
                "leads_scroll",
                JSON.stringify({
                  scrollY: window.scrollY,
                  tab: activeFilter,
                  leadId: lead.id,
                }),
              );
              window.open(`/lead/${lead.id}`, "_blank", "noopener,noreferrer");
            }}
          >
            <Card className={selectMode && isSelected ? "ring-2 ring-[#00a8b4] bg-[#00a8b4]/5" : ""}>
              <div
                className="space-y-3 p-3"
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectMode) {
                    onToggleSelect?.(lead.id);
                  } else {
                    handleLeadClick(lead.id);
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {selectMode && (
                          isSelected ? (
                            <CheckCircle2 size={18} className="text-[#00a8b4] shrink-0" />
                          ) : (
                            <Circle size={18} className="text-gray-300 shrink-0" />
                          )
                        )}
                        <h3 className="mb-1 font-semibold capitalize">
                          {lead.name || "No name"}
                        </h3>
                      </div>
                      <Badge
                        variant="secondary"
                        className="ml-4 capitalize whitespace-nowrap"
                      >
                        {String(lead.deal_status || "").replace(/_/g, " ")}
                      </Badge>
                    </div>

                    <p className="flex gap-1 text-sm">
                      <MapPinCheckInside
                        size={16}
                        className="text-muted-foreground"
                      />
                      <span className="text-sm text-muted-foreground max-w-[250px]">
                        {lead.address || "Not specified"}
                      </span>
                    </p>

                    <div className="grid grid-cols-2">
                      <p className="flex flex-col my-1 text-sm">
                        <span className="text-xs font-medium text-muted-foreground">
                          Lead Source:
                        </span>
                        <span className="text-foreground">
                          {lead.reference_label || lead.reference || "Not specified"}
                        </span>
                      </p>
                      <p className="flex flex-col my-1 text-sm">
                        <span className="text-xs font-medium text-muted-foreground">
                          Agent:
                        </span>
                        <span className="text-foreground">
                          {lead.agent_name || "Unassigned"}
                        </span>
                      </p>
                    </div>

                    {latestComment && (
                      <p className="flex flex-col my-1 text-sm">
                        <span className="text-xs font-medium text-muted-foreground">
                          Latest Comment:
                        </span>
                        <span className="text-foreground break-words line-clamp-2">
                          {latestComment.comment_text ||
                            JSON.stringify(latestComment)}
                        </span>
                      </p>
                    )}

                    {/* Created & Last Updated badges */}
                    <div className="flex items-center gap-2 mt-1.5">
                      {lead.created_at && (
                        <span className="inline-flex items-center gap-1 text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                          <Calendar size={8} />
                          {formatCreatedDate(lead.created_at)}
                        </span>
                      )}
                      {lead.updated_at && lead.updated_at !== lead.created_at && (
                        <span className="inline-flex items-center gap-1 text-[9px] text-blue-400 bg-blue-50 px-1.5 py-0.5 rounded">
                          <Clock size={8} />
                          {timeAgo(lead.updated_at)}
                        </span>
                      )}
                    </div>

                    {!selectMode && (
                      <div className="flex items-center justify-start gap-3 mt-2">
                        <button
                          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-50 border border-blue-500 hover:bg-blue-100 transition active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `tel:${lead.phone_number}`;
                          }}
                          title="Call"
                        >
                          <Phone size={14} className="text-blue-600" />
                        </button>
                        <button
                          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-red-50 border border-red-500 hover:bg-red-100 transition active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `mailto:${lead.email}`;
                          }}
                          title="Email"
                        >
                          <Mail size={14} className="text-red-600" />
                        </button>
                        <button
                          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 border border-emerald-500 hover:bg-emerald-100 transition active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            assignScrollLeadId.current = lead.id;
                            setSelectedLead(lead);
                            setIsAssignModalOpen(true);
                          }}
                          title="Reassign Lead"
                        >
                          <UserPlus size={14} className="text-emerald-600" />
                        </button>
                        <VoiceUpdateButton
                          leadId={lead.id}
                          leadName={lead.name}
                          onUpdated={onLeadUpdated}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );
      })}

      <div ref={setSentinelRef} className="h-1" />

      {isFetchingMore && (
        <div className="flex justify-center py-4">
          <div className="w-5 h-5 border-2 border-[#00a8b4] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!hasMore && visibleLeads.length > 0 && !isInitialLoading && (
        <p className="text-center text-xs text-slate-400 pb-4">
          You've reached the end
        </p>
      )}
    </div>
  );
};

export default LeadCardList;
